import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { FaSearch, FaSpinner, FaFileInvoice } from "react-icons/fa";
import { searchInvoices, getInvoiceDetail } from "redux/erp/paymentRequestSlice";
import Clickable from "./Clickable";
import FileGalleryModal from "./FileGalleryModal";
import { formatDate } from "./fileMeta";

const money = (v) => (v === null || v === undefined || v === "" ? null : (Number(v) || 0).toLocaleString("en-US"));

// Tên file hóa đơn điện tử: <MST người bán>_<MST người mua>_<ký hiệu>_<số>_new.pdf.
// OCR không đọc được tên người bán thì ít nhất còn mã số thuế để nhận ra.
const sellerTaxCode = (filename) => String(filename || "").match(/^(\d{10}(?:-\d{3})?)_/)?.[1] || null;

const DetailRow = ({ label, children, strong }) => (
  <div className="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-py-2 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border last:tw-border-b-0">
    <span className="tw-text-[12px] tw-text-geist-fg-3 tw-flex-shrink-0">{label}</span>
    <span className={`tw-text-right tw-text-[13px] tw-break-words tw-min-w-0 ${strong ? "tw-font-bold tw-text-geist-fg" : "tw-text-geist-fg-2"}`}>
      {children || <span className="tw-text-geist-fg-3">—</span>}
    </span>
  </div>
);

DetailRow.propTypes = {
  label: PropTypes.node,
  children: PropTypes.node,
  strong: PropTypes.bool,
};

/**
 * Tìm hóa đơn từ doc-flatform (proxy /invoice_search) — panel INLINE (không popover).
 * Debounce 400ms. Mỗi kết quả:
 *   - click dòng  -> xem trước PDF ở pane bên phải form (onPreview)
 *   - "View"      -> mở modal toàn màn hình: PDF + chi tiết đã đọc từ hóa đơn, chuyển
 *                    qua lại giữa các kết quả, bấm Add ngay trong modal — để người lập
 *                    xem kỹ trước khi thêm thay vì thêm mù
 *   - "Add"       -> thêm thành 1 dòng (onAdd); thêm nhiều cái liên tiếp, danh sách không đóng
 *
 * Props:
 *   onPreview(document_id, item)
 *   onAdd(document_id, item)
 *   addedIds: Set<string>   — docId đã thêm (hiện "Added", disable)
 *   activeId: string|null   — docId đang preview (highlight)
 *   resolveDetail(id)       — tuỳ chọn; form truyền vào để dùng chung cache với Add
 *   paymentRequestId        — phiếu đang sửa: hoá đơn của chính nó không tính là "đã dùng"
 *
 * Một hoá đơn chỉ được một phiếu còn sống thanh toán: kết quả có `used_by` (phiếu
 * đang giữ) thì hiện "Used on PRQ-…" và không cho Add.
 */
const usedLabel = (it) => (it.used_by ? `Used on ${it.used_by.code}` : null);

const InvoiceSearchPanel = ({ onPreview, onAdd, addedIds, activeId, resolveDetail, paymentRequestId, placeholder = "Search invoice (Inv No / vendor)..." }) => {
  const dispatch = useDispatch();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [viewIndex, setViewIndex] = useState(null);
  const [details, setDetails] = useState({}); // document_id -> { loading, data, error }
  const [adding, setAdding] = useState(null);
  const timer = useRef();
  const addingRef = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults([]); setSearched(false); return undefined; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = paymentRequestId ? { q, size: 20, payment_request_id: paymentRequestId } : { q, size: 20 };
        const res = await dispatch(searchInvoices(params)).unwrap();
        setResults(res.items || []);
      } catch { setResults([]); }
      finally { setLoading(false); setSearched(true); }
    }, 400);
    return () => clearTimeout(timer.current);
  }, [q, dispatch, paymentRequestId]);

  const isAdded = (id) => Array.from(addedIds || []).some((addedId) => String(addedId) === String(id));

  // Lấy chi tiết (view_url + dữ liệu đọc từ hóa đơn) cho hóa đơn đang xem trong modal.
  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    setDetails((d) => (d[id] ? d : { ...d, [id]: { loading: true } }));
    try {
      const inv = resolveDetail ? await resolveDetail(id) : await dispatch(getInvoiceDetail(id)).unwrap();
      setDetails((d) => ({ ...d, [id]: { data: inv } }));
    } catch (e) {
      setDetails((d) => ({ ...d, [id]: { error: e?.error || e?.message || "Could not load this invoice from Doc Search." } }));
    }
  }, [dispatch, resolveDetail]);

  const viewed = viewIndex !== null ? results[viewIndex] : null;
  useEffect(() => {
    if (viewed && !details[viewed.document_id]) loadDetail(viewed.document_id);
  }, [viewed, details, loadDetail]);

  const openView = (idx) => setViewIndex(idx);

  // onAdd trả false khi thêm hỏng (form đã báo lỗi). Thêm được thì đóng modal View
  // để người dùng thấy ngay dòng vừa vào phiếu và nút đổi sang "Added".
  const add = async (it, { closeView = false } = {}) => {
    if (isAdded(it.document_id) || it.used_by || addingRef.current !== null) return;
    // Lock before the async detail lookup, including clicks before React renders.
    addingRef.current = it.document_id;
    setAdding(it.document_id);
    try {
      const ok = await onAdd?.(it.document_id, it);
      if (closeView && ok !== false) setViewIndex(null);
    } finally { addingRef.current = null; setAdding(null); }
  };

  const galleryItems = results.map((it) => {
    const st = details[it.document_id] || {};
    const inv = st.data || {};
    return {
      key: it.document_id,
      name: it.filename || inv.filename || it.inv_no || "Invoice",
      url: inv.view_url || inv.download_url || null,
      contentType: "application/pdf",
      subtitle: [it.vendor_name, it.inv_no && `#${it.inv_no}`].filter(Boolean).join(" · "),
      // Thumbnail: số hóa đơn + người bán thay cho tên file (chỉ là dãy mã số thuế).
      // Thẻ hồ sơ ở dải dưới: mỗi kết quả tìm kiếm là một hóa đơn riêng.
      thumbLabel: it.inv_no ? `Invoice #${it.inv_no}` : "Invoice (no number)",
      thumbSub: inv.vendor_name || it.vendor_name || (sellerTaxCode(it.filename) && `Tax code ${sellerTaxCode(it.filename)}`) || "",
      thumbMeta: [formatDate(it.inv_date), money(it.amount)].filter(Boolean).join(" · "),
      thumbBadge: isAdded(it.document_id) ? "Added" : usedLabel(it),
      loading: !st.data && !st.error,
      error: st.error && "Could not load this invoice from Doc Search. It may have been re-indexed — search again.",
    };
  });

  // Panel thông tin bên phải modal: dữ liệu đọc từ hóa đơn + nút Add.
  // Chỉ hiện trường đọc được; trường OCR không đọc ra gom thành một dòng ghi chú —
  // một cột toàn dấu "—" làm người xem tưởng hóa đơn trống.
  const renderSide = (_item, idx) => {
    const it = results[idx];
    if (!it) return null;
    const st = details[it.document_id] || {};
    const inv = st.data || {};
    const added = isAdded(it.document_id);
    const usedBy = it.used_by;
    const currency = inv.currency || it.currency || "VND";
    const withCcy = (v) => (money(v) ? `${money(v)} ${currency}` : null);
    const taxCode = sellerTaxCode(inv.filename || it.filename);
    const vendor = inv.vendor_name || it.vendor_name;
    const loadingDetail = !st.data && !st.error;

    const fields = [
      { label: "Invoice no.", value: inv.inv_no || it.inv_no, strong: true },
      { label: "Serial", value: inv.serial },
      { label: "Invoice date", value: formatDate(inv.inv_date || it.inv_date) },
      { label: "Seller tax code", value: taxCode },
      { label: "Amount before VAT", value: withCcy(inv.amount_excl_vat) },
      { label: "VAT", value: withCcy(inv.vat_amount) },
      { label: "Total", value: withCcy(inv.total_amount ?? it.amount), strong: true },
      { label: "Payment method", value: inv.payment_method },
      { label: "Bank account", value: inv.bank_account },
      { label: "Description", value: inv.description || it.description },
    ];
    const found = fields.filter((f) => f.value);
    const missing = fields.filter((f) => !f.value).map((f) => f.label);

    return (
      <>
        <div className="tw-px-5 tw-py-4 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border">
          <div className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-geist-fg-3">Seller</div>
          <div className="tw-mt-1 tw-text-[15px] tw-font-semibold tw-leading-snug tw-text-geist-fg tw-break-words">
            {vendor || (taxCode ? `Tax code ${taxCode}` : "Not read from the invoice")}
          </div>
          {!vendor && taxCode && (
            <div className="tw-mt-0.5 tw-text-[12px] tw-text-geist-fg-3">Name not read from the invoice — check the PDF.</div>
          )}
        </div>
        <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-px-5 tw-py-2">
          {loadingDetail ? (
            <div className="tw-py-8 tw-text-center tw-text-sm tw-text-geist-fg-3"><FaSpinner className="tw-animate-spin tw-mr-2 tw-inline" />Reading invoice…</div>
          ) : (
            <>
              {found.map((f) => <DetailRow key={f.label} label={f.label} strong={f.strong}>{f.value}</DetailRow>)}
              {missing.length > 0 && (
                <p className="tw-mt-3 tw-mb-0 tw-text-[12px] tw-leading-relaxed tw-text-geist-fg-3">
                  <i className="bi bi-info-circle tw-mr-1" />
                  Not read from this invoice: {missing.join(", ")}. Check them on the PDF.
                </p>
              )}
              {st.error && (
                <p className="tw-mt-3 tw-mb-0 tw-text-[12px] tw-text-[#8e2a18]">
                  <i className="bi bi-exclamation-triangle tw-mr-1" />Details could not be loaded — only the search summary is shown.
                </p>
              )}
            </>
          )}
        </div>
        <div className="tw-px-5 tw-py-4 tw-border-0 tw-border-t tw-border-solid tw-border-geist-border tw-bg-geist-bg-2">
          {usedBy && !added && (
            <p role="note" className="tw-m-0 tw-mb-3 tw-text-[12px] tw-leading-relaxed tw-text-[#a05e03]">
              <i className="bi bi-lock tw-mr-1" />
              Already on <a href={`/payment-requests/${usedBy.id}`} target="_blank" rel="noreferrer" className="tw-font-semibold tw-text-[#a05e03] tw-underline">{usedBy.code}</a>
              {usedBy.status ? ` (${usedBy.status})` : ""} — a document can be paid by one request only.
            </p>
          )}
          <Clickable
            onClick={() => add(it, { closeView: true })}
            disabled={added || !!usedBy || !!st.error || adding !== null}
            aria-busy={adding === it.document_id || undefined}
            className={`tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-1.5 tw-h-10 tw-rounded-geist-sm tw-text-[14px] tw-font-semibold tw-border tw-border-solid ${
              added || usedBy || st.error
                ? "tw-bg-geist-100 tw-text-geist-fg-3 tw-border-geist-border"
                : "tw-bg-geist-accent tw-text-white tw-border-geist-accent hover:tw-bg-geist-accent-2"}`}>
            {adding === it.document_id ? <FaSpinner className="tw-animate-spin" /> : <i className={`bi ${added ? "bi-check-lg" : "bi-plus-lg"}`} />}
            {added ? "Added to this request" : usedBy ? `Used on ${usedBy.code}` : adding === it.document_id ? "Adding…" : "Add to request"}
          </Clickable>
          <p className="tw-text-[11px] tw-text-geist-fg-3 tw-m-0 tw-mt-2 tw-text-center">
            Adds it to the request and closes this view · ← → to browse results
          </p>
        </div>
      </>
    );
  };

  return (
    <div className="tw-flex tw-flex-col tw-min-h-0 tw-h-full">
      {/* Search input — underline style Geist */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-border-0 tw-border-b tw-border-solid tw-border-geist-border-2 focus-within:tw-border-geist-accent tw-transition-colors tw-flex-shrink-0">
        {loading ? <FaSpinner size={12} className="tw-text-geist-fg-3 tw-animate-spin tw-flex-shrink-0" />
          : <FaSearch size={12} className="tw-text-geist-fg-3 tw-flex-shrink-0" />}
        <input
          type="text"
          autoComplete="off"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder}
          style={{ boxShadow: "none", WebkitAppearance: "none", borderRadius: 0, backgroundColor: "transparent" }}
          className="tw-flex-1 tw-min-w-0 tw-appearance-none tw-border-0 tw-bg-transparent tw-py-2.5 tw-text-sm tw-text-geist-fg placeholder:tw-text-geist-fg-3 focus:tw-outline-none"
        />
        {q && (
          <Clickable onClick={() => setQ("")} className="tw-text-geist-fg-3 hover:tw-text-geist-fg tw-flex-shrink-0">
            <i className="bi bi-x-lg tw-text-[11px]" />
          </Clickable>
        )}
      </div>

      {/* Results — inline list (scroll riêng) */}
      <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw--mx-1 tw-mt-2">
        {q.trim().length < 2 ? (
          <div className="tw-px-3 tw-py-6 tw-text-sm tw-text-geist-fg-3 tw-text-center">
            Type at least 2 characters to search.
          </div>
        ) : loading && results.length === 0 ? (
          <div className="tw-px-3 tw-py-6 tw-text-sm tw-text-geist-fg-3 tw-flex tw-items-center tw-justify-center tw-gap-2">
            <FaSpinner className="tw-animate-spin" /> Searching…
          </div>
        ) : results.length === 0 ? (
          <div className="tw-px-3 tw-py-6 tw-text-sm tw-text-geist-fg-3 tw-text-center">
            {searched ? "No invoice found." : "Type to search."}
          </div>
        ) : (
          <div className="tw-divide-y tw-divide-geist-border">
            {results.map((it, idx) => {
              const added = isAdded(it.document_id);
              const used = !added && usedLabel(it);
              const active = activeId && activeId === it.document_id;
              return (
                <div
                  key={it.document_id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onPreview?.(it.document_id, it)}
                  title="Click to preview on the right"
                  className={`tw-flex tw-items-start tw-gap-2.5 tw-px-3 tw-py-3 tw-min-h-[56px] tw-cursor-pointer tw-select-none tw-transition-colors ${active ? "tw-bg-geist-100" : "hover:tw-bg-geist-bg-2"}`}
                >
                  <span className="tw-inline-flex tw-h-8 tw-w-8 tw-mt-0.5 tw-items-center tw-justify-center tw-rounded-geist-sm tw-bg-geist-100 tw-text-geist-accent tw-flex-shrink-0">
                    <FaFileInvoice size={13} />
                  </span>
                  <div className="tw-flex-1 tw-min-w-0">
                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                      <span className="tw-text-sm tw-font-semibold tw-text-geist-fg tw-truncate">
                        {it.vendor_name || it.inv_no || it.filename || "(untitled)"}
                      </span>
                      {it.amount ? <span className="tw-text-base tw-font-semibold tw-text-primary tw-flex-shrink-0 tw-tabular-nums">{money(it.amount)}</span> : null}
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-[12px] tw-text-geist-fg-3 tw-min-w-0">
                      {it.inv_no && <span className="tw-font-semibold tw-text-geist-fg-2 tw-flex-shrink-0">#{it.inv_no}</span>}
                      {it.inv_date && <span className="tw-flex-shrink-0">· {it.inv_date}</span>}
                      {it.description && <span className="tw-truncate">· {it.description}</span>}
                    </div>
                  </div>
                  {/* View — xem kỹ trước khi thêm */}
                  <Clickable
                    stopPropagation
                    onClick={() => openView(idx)}
                    title="View the invoice and its details"
                    className="tw-flex-shrink-0 tw-inline-flex tw-items-center tw-gap-1 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-text-[13px] tw-font-semibold tw-border tw-border-solid tw-border-geist-border tw-bg-white tw-text-geist-fg-2 hover:tw-bg-geist-100 hover:tw-text-geist-fg"
                  >
                    <i className="bi bi-eye" /> View
                  </Clickable>
                  {/* Add / Added */}
                  <Clickable
                    stopPropagation
                    onClick={() => !added && !used && add(it)}
                    disabled={added || !!used || adding !== null}
                    aria-busy={adding === it.document_id || undefined}
                    title={added ? "Already added" : used ? `${used} — a document can be paid by one request only` : "Add as line"}
                    className={`tw-flex-shrink-0 tw-inline-flex tw-items-center tw-gap-1 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-text-[13px] tw-font-semibold tw-border tw-border-solid tw-transition-colors ${
                      added || used
                        ? "tw-bg-geist-100 tw-text-geist-fg-3 tw-border-geist-border tw-cursor-default"
                        : "tw-bg-geist-accent tw-text-white tw-border-geist-accent hover:tw-bg-geist-accent-2"
                    }`}
                  >
                    {adding === it.document_id ? <FaSpinner className="tw-animate-spin" size={11} /> : <i className={`bi ${added ? "bi-check-lg" : used ? "bi-lock" : "bi-plus-lg"}`} />}
                    {added ? "Added" : used ? used : adding === it.document_id ? "Adding…" : "Add"}
                  </Clickable>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <FileGalleryModal
        show={viewIndex !== null && !!viewed}
        items={galleryItems}
        index={viewIndex ?? 0}
        onIndexChange={setViewIndex}
        onClose={() => setViewIndex(null)}
        side={renderSide}
        title="Doc Search invoice"
        stripTitle={`Other invoices in your search · ${results.length}`}
      />
    </div>
  );
};

InvoiceSearchPanel.propTypes = {
  onPreview: PropTypes.func,
  onAdd: PropTypes.func,
  addedIds: PropTypes.oneOfType([PropTypes.instanceOf(Set), PropTypes.array]),
  activeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  resolveDetail: PropTypes.func,
  paymentRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
};

export default InvoiceSearchPanel;
